<template>
  <div
    class="condition-node"
    :class="[runClass, { 'condition-node--active': active }]"
    :data-node-id="id"
  >
    <Handle type="target" :position="Position.Top" />

    <div class="condition-node__top">
      <div class="condition-node__icon">
        <BaseIcon name="split" size="16" />
      </div>
      <div class="condition-node__body">
        <span class="condition-node__kind">Condition</span>
        <h3>If / Else</h3>
        <p class="condition-node__summary">
          {{ conditionSummary || 'Niet ingesteld' }}
        </p>
      </div>
    </div>

    <div class="condition-node__branches">
      <div class="branch branch--true">
        <span class="branch__label">True</span>
        <button
          v-if="!hasTrueChild"
          type="button"
          class="branch__add-btn"
          @click.stop="addChildOnBranch('true')"
          aria-label="Add true branch step"
        >
          <BaseIcon name="plus" size="12" />
        </button>
      </div>
      <div class="branch branch--false">
        <span class="branch__label">False</span>
        <button
          v-if="!hasFalseChild"
          type="button"
          class="branch__add-btn"
          @click.stop="addChildOnBranch('false')"
          aria-label="Add false branch step"
        >
          <BaseIcon name="plus" size="12" />
        </button>
      </div>
    </div>

    <Handle
      id="true"
      type="source"
      :position="Position.Bottom"
      class="condition-node__handle condition-node__handle--true"
      :style="{ left: '25%' }"
    />
    <Handle
      id="false"
      type="source"
      :position="Position.Bottom"
      class="condition-node__handle condition-node__handle--false"
      :style="{ left: '75%' }"
    />
  </div>
</template>

<script>
  import { computed, inject } from 'vue';
  import { Handle, Position, useVueFlow } from '@vue-flow/core';
  import BaseIcon from '@shared/components/BaseIcon.vue';

  import ActionPickerModal from '../modals/ActionPickerModal.vue';
  import { useUIStore } from '@/store/uiStore';
  import {
    OPERATOR_CATALOG,
    TRIGGER_FIELD_CATALOG,
  } from '@/constants/automationFieldCatalog';

  export default {
    name: 'ConditionNode',

    components: {
      BaseIcon,
      Handle,
    },

    props: {
      id: {
        type: String,
        required: true,
      },
      data: {
        type: Object,
        required: true,
      },
      currentAutomation: {
        type: Object,
        required: true,
      },
      active: {
        type: Boolean,
        default: false,
      },
    },

    emits: ['add-node'],

    setup(props, { emit }) {
      const uiStore = useUIStore();
      const { getEdges } = useVueFlow();

      const branchEdges = computed(() =>
        getEdges.value.filter((e) => e.source === props.id)
      );

      const hasTrueChild = computed(() =>
        branchEdges.value.some((e) => e.sourceHandle === 'true')
      );
      const hasFalseChild = computed(() =>
        branchEdges.value.some((e) => e.sourceHandle === 'false')
      );

      const conditionSummary = computed(() => {
        const cond = props.data?.config?.condition;
        if (!cond?.field || !cond?.operator) return '';

        const triggerType = props.currentAutomation?.trigger_type;
        const catalog = TRIGGER_FIELD_CATALOG[triggerType] || [];
        const fieldLabel =
          catalog.find((f) => f.path === cond.field)?.label || cond.field;

        const operatorLabel =
          OPERATOR_CATALOG.find((o) => o.value === cond.operator)?.label ||
          cond.operator;

        const opMeta = OPERATOR_CATALOG.find((o) => o.value === cond.operator);
        const needsValue = opMeta ? opMeta.requiresValue : true;

        if (!needsValue) {
          return `${fieldLabel} ${operatorLabel}`;
        }

        const valueStr =
          cond.value === undefined || cond.value === null || cond.value === ''
            ? '…'
            : String(cond.value);
        return `${fieldLabel} ${operatorLabel} "${valueStr}"`;
      });

      const addChildOnBranch = (branch) => {
        const modalId = uiStore.addModal(ActionPickerModal, {
          title: `Add step on ${branch.toUpperCase()} branch`,
          description: 'Add a new step to this branch',
          width: 'lg',
          triggerType: props.currentAutomation?.trigger_type || null,
          onSubmit: (action) => {
            emit('add-node', {
              afterId: props.id,
              action,
              sourceHandle: branch,
            });
            uiStore.closeModal(modalId);
          },
        });
      };

      const runStepMap = inject(
        'automationRunStepMap',
        computed(() => ({}))
      );
      const runClass = computed(() => {
        const status = runStepMap.value?.[props.id]?.status;
        return status ? `condition-node--run-${status}` : '';
      });

      return {
        Position,
        hasTrueChild,
        hasFalseChild,
        conditionSummary,
        addChildOnBranch,
        runClass,
      };
    },
  };
</script>

<style lang="scss" scoped>
  @import '@/assets/scss/base/mixins';

  .condition-node {
    position: relative;
    width: 260px;
    padding: 0.875rem 1rem 0.75rem;
    background: #fff;
    border: 1px solid var(--slate-200);
    border-radius: 16px;
    color: var(--slate-900);
    outline: 0 solid transparent;
    box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
    transition: border-color 0.15s ease, box-shadow 0.15s ease;

    &--active {
      border-color: var(--slate-900);
      box-shadow: 0 0 0 4px var(--slate-100);
    }

    &--run-completed {
      border-color: var(--green-400);
    }

    &--run-failed {
      border-color: var(--red-400);
    }

    &--run-in_progress,
    &--run-pending {
      border-color: var(--amber-400);
    }

    &__top {
      display: flex;
      align-items: flex-start;
      gap: 0.75rem;
    }

    &__icon {
      width: 32px;
      height: 32px;
      border-radius: 10px;
      background: var(--slate-100);
      color: var(--slate-700);
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
    }

    &__body {
      min-width: 0;
      flex: 1;

      h3 {
        margin: 0;
        font-size: 0.9375rem;
        font-weight: 600;
        line-height: 1.3;
      }
    }

    &__kind {
      display: block;
      font-size: 0.6875rem;
      font-weight: 500;
      letter-spacing: 0.02em;
      text-transform: uppercase;
      color: var(--slate-500);
      margin-bottom: 0.125rem;
    }

    &__summary {
      margin: 0.25rem 0 0;
      font-size: 0.75rem;
      color: var(--slate-500);
      @include ellipsis(2);
    }

    &__branches {
      display: flex;
      justify-content: space-between;
      gap: 0.5rem;
      margin-top: 0.75rem;
      padding-top: 0.5rem;
      border-top: 1px dashed var(--slate-200);
    }

    &__handle {
      width: 12px;
      height: 12px;

      &--true {
        background: var(--green-500, #10b981);
        border-color: var(--green-600, #059669);
      }
      &--false {
        background: var(--red-500, #ef4444);
        border-color: var(--red-600, #dc2626);
      }
    }
  }

  .branch {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.35rem;

    &__label {
      font-size: 0.7rem;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.05em;
    }

    &--true .branch__label {
      color: var(--green-600, #059669);
    }
    &--false .branch__label {
      color: var(--red-600, #dc2626);
    }

    &__add-btn {
      width: 24px;
      height: 24px;
      background: #fff;
      border-radius: 50%;
      border: 1px solid var(--slate-200);
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;

      @include hover {
        background: var(--slate-50);
        border-color: var(--brand-500);
      }
    }
  }
</style>
